export const DocumentAssemblerTemplateVersionStatus = {
  DRAFT: 'DRAFT',
  PUBLISHED: 'PUBLISHED',
  ARCHIVED: 'ARCHIVED',
} as const;
export type DocumentAssemblerTemplateVersionStatus =
  (typeof DocumentAssemblerTemplateVersionStatus)[keyof typeof DocumentAssemblerTemplateVersionStatus];

export const DocumentAssemblerConditionalBranchKind = {
  IF: 'IF',
  ELSEIF: 'ELSEIF',
  ELSE: 'ELSE',
} as const;
export type DocumentAssemblerConditionalBranchKind =
  (typeof DocumentAssemblerConditionalBranchKind)[keyof typeof DocumentAssemblerConditionalBranchKind];

export interface IDocumentAssemblerRenderedError {
  code: string;
  message: string;
  documentPart: string;
  locationDescription: string;
  paragraphText: string;
  offendingText: string;
}

/** Why the server would not act on a template version, as the FE needs to render it. */
export type TDocumentAssemblerVersionRefusal =
  | {
      kind: 'NOT_USABLE';
      message: string;
      errors: IDocumentAssemblerRenderedError[];
    }
  | { kind: 'MESSAGE'; message: string };

export interface IDocumentAssemblerTemplateCategory {
  id: string;
  key: string;
  name: string;
  description: string | null;
  archivedAt: string | null;
  /** Non-archived templates only. */
  templateCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface IDocumentAssemblerTemplateVersionSummary {
  id: string;
  templateId: string;
  version: number;
  status: DocumentAssemblerTemplateVersionStatus;
  /** template.activeVersionId === this.id. The only record of which version is active. */
  isActiveVersion: boolean;
  hasPassedValidation: boolean;
  canBePublished: boolean;
  errorCount: number;
  authorGuidance: string | null;
  sourceFileId: number;
  createdAt: string;
  updatedAt: string;
}

export interface IDocumentAssemblerTemplateVersionPlaceholder {
  id: string;
  ordinal: number;
  instruction: string;
  /** Set means the author decided this is fixed text, so it is never asked for. Empty renders as nothing. */
  constantGenerationValue: string | null;
  conditionalBranchId: string | null;
}

export interface IDocumentAssemblerTemplateVersionConditionalBranch {
  id: string;
  ordinal: number;
  kind: DocumentAssemblerConditionalBranchKind;
  condition: string | null;
}

export interface IDocumentAssemblerTemplateVersionConditionalTree {
  id: string;
  ordinal: number;
  branches: IDocumentAssemblerTemplateVersionConditionalBranch[];
}

export interface IDocumentAssemblerTemplateVersionDetail
  extends IDocumentAssemblerTemplateVersionSummary {
  /** The document the user handed us verbatim when the version was created through chat; null for
   * a manual upload, which never has a separate original. */
  originalDocumentFileId: number | null;
  errors: IDocumentAssemblerRenderedError[];
  placeholders: IDocumentAssemblerTemplateVersionPlaceholder[];
  conditionalTrees: IDocumentAssemblerTemplateVersionConditionalTree[];
}

export type TDocumentAssemblerSetPlaceholderConstantGenerationValueParams =
  Readonly<{
    instruction: string;
    /** Empty renders as nothing; omit it entirely to undo the decision. */
    constantGenerationValue?: string;
  }>;

export interface IDocumentAssemblerTemplate {
  id: string;
  key: string;
  title: string;
  description: string;
  archivedAt: string | null;
  /** Resolved server-side from the template's stored category keys. */
  categories: IDocumentAssemblerTemplateCategory[];
  activeVersion: IDocumentAssemblerTemplateVersionSummary | null;
  /** Every version, archived ones included. */
  versionCount: number;
  /** Null once the author's account is gone, since the relation is SetNull. */
  createdBy: { id: string; name: string | null; email: string | null } | null;
  createdAt: string;
  updatedAt: string;
}

export interface IDocumentAssemblerTemplateCategoryListResponse {
  templateCategories: IDocumentAssemblerTemplateCategory[];
  count: number;
}

export interface IDocumentAssemblerTemplateCategoryResponse {
  templateCategory: IDocumentAssemblerTemplateCategory;
}

export interface IDocumentAssemblerTemplateListResponse {
  templates: IDocumentAssemblerTemplate[];
  count: number;
}

export interface IDocumentAssemblerTemplateResponse {
  template: IDocumentAssemblerTemplate;
}

export interface IDocumentAssemblerTemplateVersionListResponse {
  templateVersions: IDocumentAssemblerTemplateVersionSummary[];
  count: number;
}

export interface IDocumentAssemblerTemplateVersionResponse {
  templateVersion: IDocumentAssemblerTemplateVersionDetail;
}

export interface IDocumentAssemblerCreatedTemplateResponse {
  template: { id: string };
}

export interface IDocumentAssemblerCreatedTemplateCategoryResponse {
  templateCategory: { id: string };
}

export interface IDocumentAssemblerCreatedTemplateVersionResponse {
  templateVersion: { id: string };
}

export type TDocumentAssemblerCreateTemplateCategoryParams = {
  key: string;
  name: string;
  description?: string;
};

/** `key` is immutable server-side and is deliberately absent. */
export type TDocumentAssemblerUpdateTemplateCategoryParams = {
  name?: string;
  /** '' is accepted and clears it. */
  description?: string;
};

export type TDocumentAssemblerCreateTemplateParams = {
  key: string;
  title: string;
  /** Required, unlike a category's. */
  description: string;
  /** Category keys, not ids: a template stores the keys of the categories it belongs to. */
  categories: string[];
};

/** `key` is immutable server-side and is deliberately absent. */
export type TDocumentAssemblerUpdateTemplateParams = {
  title?: string;
  /** Cannot be cleared: the server declares it @IsNotEmpty when present. */
  description?: string;
  /** Category keys, not ids: a template stores the keys of the categories it belongs to. */
  categories?: string[];
};

export type TDocumentAssemblerListTemplateCategoriesParams = {
  key?: string;
  includeArchived?: boolean;
};

export type TDocumentAssemblerListTemplatesParams = {
  key?: string;
  categoryKey?: string;
  includeArchived?: boolean;
};
